/**
 * =============================================================================
 * ONLINE STATUS HOOK
 * =============================================================================
 *
 * React hook to detect and respond to network connectivity changes.
 * Returns current online/offline status and subscribes to changes.
 *
 * PATTERN REFERENCE: PHASE_0C_PATTERNS.md §8
 * TASK: 0C-39
 *
 * Features:
 * - SSR-safe (defaults to online when navigator unavailable)
 * - Subscribes to online/offline events
 * - Cleans up event listeners on unmount
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const isOnline = useOnlineStatus();
 *
 *   if (!isOnline) {
 *     return <OfflineNotice />;
 *   }
 *
 *   return <MainContent />;
 * }
 * ```
 *
 * =============================================================================
 */

import { useState, useEffect } from 'react';

/**
 * Hook to track browser online/offline status.
 *
 * @returns `true` if the browser is online, `false` if offline.
 *          Returns `true` by default for SSR or when navigator is unavailable.
 */
export function useOnlineStatus(): boolean {
  // Always initialize to true for SSR/hydration consistency.
  // This ensures server and client render the same initial state.
  // The actual online status is synced immediately in useEffect.
  const [isOnline, setIsOnline] = useState(true);

  useEffect(() => {
    // SSR guard - no window events during server-side rendering
    if (typeof window === 'undefined') {
      return;
    }

    // Sync actual status immediately on mount
    // This runs after hydration completes, avoiding mismatch
    setIsOnline(navigator.onLine);

    const handleOnline = (): void => setIsOnline(true);
    const handleOffline = (): void => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
}
